import { TFunction } from 'i18next';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';
import { DatasetPoint } from '../components/Graphs/common/Graphs/AreaChart';
import { UsageTypeValue, UsageType } from '../components/UsageTypeSwitch/UsageTypeSwitch';
import { parseCompactNumber } from './parseCompactNumber';
import { parseCompactTime } from './parseCompactTime';

const DOT_BLUE_COLOR = '#597b82';
const DOT_GREY_COLOR = '#c0cacc';
const DOT_RED_COLOR = '#f67d85';
export const BEFORE_PROJECTED_DOT_RED_COLOR = '#EB5761';

const BACKGROUND_BLUE_COLOR = '#b7dbf0';
const BACKGROUND_GREEN_COLOR = '#caedce';
const BACKGROUND_DARK_GREY_COLOR = '#edf1f2';
const BACKGROUND_LIGHT_GREY_COLOR = '#f7f9fa';

export const LEGEND_BLUE_COLOR = '#6fb7e1';
export const LEGEND_GREEN_COLOR = '#96db9d';

const checkForAllZeros = (data: DatasetPoint[]) =>
  data.every((item) => Number.parseFloat(item.value) === 0);

type LegendItem = {
  color: string;
  description: string;
};

type Segment = {
  start: number;
  end: number;
};

export type UsageChartData = {
  data: {
    label: string;
    value: { mt: string; mo: string; total: string };
  }[];
  limit?: number;
  unit: string;
  lastUpdatedAt: string;
  segments: {
    data: Segment;
    projected: Segment;
  };
};

export type UsageDto = {
  chartData: UsageChartData;
};

const getTotalPointColor = ({
  isAboveLimit,
  isPointBeforeProjected,
}: {
  isAboveLimit: boolean;
  isPointBeforeProjected: boolean;
}) => {
  if (isAboveLimit) {
    return isPointBeforeProjected ? BEFORE_PROJECTED_DOT_RED_COLOR : DOT_RED_COLOR;
  }

  return isPointBeforeProjected ? DOT_BLUE_COLOR : DOT_GREY_COLOR;
};

const getTotalBorderColor = ({
  isAboveLimit,
  isBackgroundBeforeProjected,
}: {
  isAboveLimit: boolean;
  isBackgroundBeforeProjected: boolean;
}) => {
  if (isAboveLimit) {
    return isBackgroundBeforeProjected ? BEFORE_PROJECTED_DOT_RED_COLOR : DOT_RED_COLOR;
  }

  return isBackgroundBeforeProjected ? DOT_BLUE_COLOR : DOT_GREY_COLOR;
};

const getProjectionFlags = (label: string, dataEnd?: number) => {
  const currentDay = new Date(label).getDate();

  const isPointBeforeProjected = dataEnd ? currentDay <= dataEnd : true;
  const isBackgroundBeforeProjected = dataEnd ? currentDay < dataEnd : true;

  return {
    currentDay,
    isPointBeforeProjected,
    isBackgroundBeforeProjected,
    solidLine: isBackgroundBeforeProjected,
  };
};

const getParsedTotal = (mo: string, mt: string, total: string) => {
  const parsedMO = Number.parseFloat(mo);
  const parsedMT = Number.parseFloat(mt);

  if (parsedMO > 0 && parsedMT > 0) {
    return parsedMO + parsedMT;
  }

  return Number.parseFloat(total);
};

type BuildMoPointParams = {
  label: string;
  value: string;
  isPointBeforeProjected: boolean;
  isBackgroundBeforeProjected: boolean;
};

const buildMoPoint = ({
  label,
  value,
  isPointBeforeProjected,
  isBackgroundBeforeProjected,
}: BuildMoPointParams): DatasetPoint => {
  const backgroundColor = isBackgroundBeforeProjected
    ? BACKGROUND_GREEN_COLOR
    : BACKGROUND_LIGHT_GREY_COLOR;

  return {
    label,
    value,
    pointColor: isPointBeforeProjected ? BACKGROUND_GREEN_COLOR : BACKGROUND_LIGHT_GREY_COLOR,
    backgroundColor,
    borderColor: backgroundColor,
  };
};

type BuildMtPointParams = {
  label: string;
  value: string;
  isPointBeforeProjected: boolean;
  isBackgroundBeforeProjected: boolean;
  solidLine: boolean;
};

const buildMtPoint = ({
  label,
  value,
  isPointBeforeProjected,
  isBackgroundBeforeProjected,
  solidLine,
}: BuildMtPointParams): DatasetPoint => {
  const backgroundColor = isBackgroundBeforeProjected
    ? LEGEND_BLUE_COLOR
    : BACKGROUND_DARK_GREY_COLOR;

  return {
    label,
    value,
    pointColor: isPointBeforeProjected ? LEGEND_BLUE_COLOR : BACKGROUND_DARK_GREY_COLOR,
    backgroundColor,
    borderColor: backgroundColor,
    solidLine,
  };
};

type BuildTotalPointParams = {
  label: string;
  total: number;
  isAboveLimit: boolean;
  isPointBeforeProjected: boolean;
  isBackgroundBeforeProjected: boolean;
  solidLine: boolean;
};

const buildTotalPoint = ({
  label,
  total,
  isAboveLimit,
  isPointBeforeProjected,
  isBackgroundBeforeProjected,
  solidLine,
}: BuildTotalPointParams): DatasetPoint => {
  return {
    label,
    value: total.toFixed(3),
    pointColor: getTotalPointColor({ isAboveLimit, isPointBeforeProjected }),
    backgroundColor: isBackgroundBeforeProjected
      ? BACKGROUND_BLUE_COLOR
      : BACKGROUND_DARK_GREY_COLOR,
    borderColor: getTotalBorderColor({
      isAboveLimit,
      isBackgroundBeforeProjected,
    }),
    solidLine,
  };
};

const buildLegend = (
  hasMOData: boolean,
  hasMTData: boolean,
  hasTotalData: boolean,
  isDataType: boolean,
  t: TFunction,
): LegendItem[] => {
  if (hasMOData || hasMTData) {
    return [
      {
        color: LEGEND_GREEN_COLOR,
        description: isDataType ? t('common.upload') : 'MO',
      },
      {
        color: LEGEND_BLUE_COLOR,
        description: isDataType ? t('common.download') : 'MT',
      },
    ];
  }

  if (hasTotalData) {
    return [{ color: LEGEND_BLUE_COLOR, description: 'Total' }];
  }

  return [];
};

type BuildDatasetsParams = {
  hasTotalData: boolean;
  hasMOData: boolean;
  hasMTData: boolean;
  totalData: DatasetPoint[];
  moData: DatasetPoint[];
  mtData: DatasetPoint[];
  isDataType: boolean;
};

const buildDatasets = (
  {
    hasTotalData,
    hasMOData,
    hasMTData,
    totalData,
    moData,
    mtData,
    isDataType,
  }: BuildDatasetsParams,
  t: TFunction,
) => {
  const datasets = [];

  if (hasTotalData) {
    datasets.push({
      label: t('common.total'),
      data: totalData,
      displayBorder: true,
      order: 2,
      fill: true,
    });
  }

  if (hasMOData) {
    datasets.push({
      label: isDataType ? t('common.upload') : 'MO',
      data: moData,
      displayBorder: false,
      order: 1,
      fill: true,
    });
  }

  if (hasMTData) {
    datasets.push({
      label: isDataType ? t('common.download') : 'MT',
      data: mtData,
      displayBorder: false,
      order: 1,
      fill: false,
    });
  }

  return datasets;
};

export const mapUsageDTOtoAreaChartData = (
  data: UsageChartData,
  usageType: UsageTypeValue,
  t: TFunction,
) => {
  const isDataType = usageType === UsageType.Data;

  const totalData: DatasetPoint[] = [];
  const mtData: DatasetPoint[] = [];
  const moData: DatasetPoint[] = [];

  data.data.forEach((dataPoint) => {
    const { isPointBeforeProjected, isBackgroundBeforeProjected, solidLine } = getProjectionFlags(
      dataPoint.label,
      data.segments?.data.end,
    );

    const parsedTotal = getParsedTotal(
      dataPoint.value.mo,
      dataPoint.value.mt,
      dataPoint.value.total,
    );

    const isAboveLimit = data.limit ? parsedTotal >= data.limit : false;
    const label = new Date(dataPoint.label).toLocaleDateString();

    // MO Data
    moData.push(
      buildMoPoint({
        label,
        value: dataPoint.value.mo,
        isPointBeforeProjected,
        isBackgroundBeforeProjected,
      }),
    );

    // MT Data
    mtData.push(
      buildMtPoint({
        label,
        value: dataPoint.value.mt,
        isPointBeforeProjected,
        isBackgroundBeforeProjected,
        solidLine,
      }),
    );

    // Total data
    totalData.push(
      buildTotalPoint({
        label,
        total: parsedTotal,
        isAboveLimit,
        isPointBeforeProjected,
        isBackgroundBeforeProjected,
        solidLine,
      }),
    );
  });

  const hasMOData = moData.length > 0 && !checkForAllZeros(moData);
  const hasMTData = mtData.length > 0 && !checkForAllZeros(mtData);
  const hasTotalData = totalData.length > 0;

  const xAxisLabels: string[] = data.data.map(
    (dataPoint) => `${new Date(dataPoint.label).getDate().toString()}\n`, // added a new line in order to make both usage charts the same height
  );

  return {
    datasets: buildDatasets(
      { hasTotalData, hasMOData, hasMTData, totalData, moData, mtData, isDataType },
      t,
    ),
    lastUpdated: data.lastUpdatedAt,
    xAxisLabels,
    yAxisLabel: data.unit.toLowerCase() === 'none' ? '' : data.unit,
    limit: data.limit,
    legend: buildLegend(hasMOData, hasMTData, hasTotalData, isDataType, t),
    realDataEndIndex: data.segments?.data.end
      ? data.data.findIndex((dp) => new Date(dp.label).getDate() === data.segments?.data.end)
      : undefined,
  };
};

export const usageTooltipValueFormatter = (
  value: number,
  usageType: UsageTypeValue,
  unit?: string,
) => {
  const defaultValue = `${toLocaleBrowserSettings(value)} ${unit || ''}`;

  if (!unit) {
    return defaultValue;
  }

  if (usageType === UsageType.Voice) {
    return parseCompactTime(value, unit);
  } else if (usageType === UsageType.Sms) {
    return parseCompactNumber(value, unit);
  } else {
    return defaultValue;
  }
};
